import { joiResolver } from '@hookform/resolvers/joi';
import {
  createPriceItem,
  deletePriceItem,
  getPriceItemById,
  updatePriceItem,
} from 'api/v2/revenues';
import { Button } from 'components/atoms/Button';
import CustomLoader from 'components/molecules/CustomLoader.component';
import SimpleError from 'components/molecules/SimpleError.component';
import type { SlideOver2Props } from 'components/molecules/SlideOver2.component';
import SlideOver2 from 'components/molecules/SlideOver2.component';
import { worldThemeContext } from 'hooks/shared/useWorldTheme';
import useConfirm from 'hooks/useConfirm';
import type {
  CloseMeta,
  FormSlideOverManagerApi,
} from 'hooks/useFormSlideOverManager';
import {
  forceClose,
  naturalClose,
  useFormSlideOverManager,
} from 'hooks/useFormSlideOverManager';
import { isEmpty } from 'lodash';
import { AnimatePresence } from 'motion/react';
import type { FC, MutableRefObject } from 'react';
import { useId, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useQuery from 'hooks/react-query/useQuery';

import { RevenuesCacheKeys } from 'types/cacheKeys.types';
import { PriceItem, PriceItemFormValues } from 'types/invoice.types';
import { RevenueItemUnit } from 'types/revenues.types';
import { WEBAPP_PRICEITEM_CLICKEDDELETE } from 'utils/amplitude/events/priceList.amplitude';
import { cleanEmptyValues } from 'utils/helpers';
import { TrashIcon } from 'utils/icons';
import showToast from 'utils/toast';
import Form from './Form';
import { getDefaultValue, priceItemValidationSchema } from './utils';

export type ItemInput = string | Partial<PriceItem>;

type ExtraFormWrapperProps = {
  afterSubmit?: (result: any) => void;
  afterDelete?: () => void;
  onClose?: (meta: CloseMeta) => void;
  showBackdrop?: boolean;
  enableFocusTrap?: boolean;
};

type FormWrapperProps = {
  item: ItemInput;
  isFormDirtyRef: any;
} & ExtraFormWrapperProps &
  Partial<Omit<SlideOver2Props, 'onClose'>>;

const unSavedChangesDefaultSettings = {
  className: 'text-center font-semibold',
  buttonsWrapperClassName: 'justify-center',
  buttonClassName: 'min-w-[90px]',
};

const FormWrapper: FC<FormWrapperProps> = ({
  item,
  onClose,
  isFormDirtyRef,
  afterSubmit,
  afterDelete,
  showBackdrop,
  enableFocusTrap,
}) => {
  const { t } = useTranslation();

  const [confirmContent, confirmApi] = useConfirm();

  const formAPI = useForm({
    mode: 'onChange',
    resolver: joiResolver(priceItemValidationSchema, {
      abortEarly: false,
      allowUnknown: true,
    }),
  });

  const getPriceItem = (item: ItemInput) => {
    if (typeof item === 'string') return getPriceItemById(item);
    return Promise.resolve(item);
  };

  const itemAPI = useQuery(
    [RevenuesCacheKeys.RevenueItemById, useId()],
    () => {
      return getPriceItem(item).then((data) => {
        formAPI.reset(getDefaultValue(data));
        return data;
      });
    },
    {
      gcTime: 0,
    },
  );

  const isLoading = itemAPI.isLoading;
  const isError = itemAPI.isError;
  const retry = () => {
    if (itemAPI.isError) itemAPI.refetch();
  };

  const isEdit = !!itemAPI.data?._id;

  const isDirty = !isEmpty(formAPI.formState.dirtyFields);
  useImperativeHandle(isFormDirtyRef, () => isDirty, [isDirty]);

  const onSubmit = async (_values: PriceItemFormValues) => {
    const values: PriceItemFormValues = cleanEmptyValues(
      {
        ..._values,
        unit: _values.unit || RevenueItemUnit.items,
      },
      ['VATRate'],
    );

    const submitPromise = values._id
      ? updatePriceItem(values._id, values)
      : createPriceItem(values).then((data) => {
          showToast({ title: t('priceList.added') }, { type: 'success' });
          return data;
        });

    return submitPromise.then((result) => {
      onClose?.(forceClose);
      afterSubmit?.(result);
    });
  };

  const onDelete = (_id: string): Promise<boolean> => {
    return new Promise((resolve) => {
      confirmApi.open({
        ...unSavedChangesDefaultSettings,
        message: t('priceList.confirm_delete'),
        onConfirm: () =>
          deletePriceItem(_id).then(() => {
            showToast({ title: t('priceList.deleted') }, { type: 'success' });
            resolve(true);
          }),
        onDecline: () => {
          confirmApi.close();
          resolve(false);
        },
      });
    });
  };

  return (
    <>
      {confirmContent}
      <SlideOver2
        showLeftPanelUI={!!showBackdrop}
        shouldTrapFocus={enableFocusTrap}
        onClose={() => onClose?.(naturalClose)}
        renderRightPanel={() => {
          if (isLoading) {
            return <CustomLoader className="h-full" />;
          }
          if (isError) {
            return <SimpleError messageClassName="!text-xl" onRetry={retry} />;
          }
          if (!itemAPI.data) return null;
          return (
            <Form
              formAPI={formAPI}
              item={itemAPI.data}
              onSubmit={onSubmit}
              headerMeta={
                <>
                  <div className="relative z-10 pointer-events-auto mt-5">
                    <h1 className="font-avenir text-5xl text-primary-700 font-bold tracking-tight leading-tight truncate mt-5 mb-5">
                      {t(
                        isEdit
                          ? 'priceItem.form.edit.title'
                          : 'priceItem.form.create.title',
                      )}
                    </h1>
                  </div>
                  <div className="border-b border-primary-100" />
                </>
              }
              footerMeta={
                isEdit && (
                  <div className="mt-4">
                    <Button
                      tracingEvent={WEBAPP_PRICEITEM_CLICKEDDELETE}
                      structure="text"
                      onClick={() =>
                        onDelete(itemAPI.data._id as string).then(
                          (isDeleted) => {
                            if (isDeleted) {
                              onClose?.(forceClose);
                              afterDelete?.();
                            }
                          },
                        )
                      }
                    >
                      <TrashIcon className="w-5 mr-2" />
                      <span className="font-semibold">{t('delete')}</span>
                    </Button>
                  </div>
                )
              }
            />
          );
        }}
      />
    </>
  );
};

type PriceListFormSlideOverProps = {
  apiRef: MutableRefObject<FormSlideOverManagerApi<ItemInput> | undefined>;
  onOpen?: (doc: ItemInput) => void;
} & ExtraFormWrapperProps;

const PriceListFormSlideOver: FC<PriceListFormSlideOverProps> = ({
  apiRef,
  afterSubmit,
  afterDelete,
  onClose,
  onOpen,
  enableFocusTrap,
  showBackdrop,
}) => {
  const { doc, api, isFormDirtyRef, confirmContent, formKey, options } =
    useFormSlideOverManager<ItemInput>({
      apiRef,
      onClose,
      onOpen,
    });

  return (
    <worldThemeContext.Provider value="revenues">
      {confirmContent}
      <AnimatePresence mode="wait">
        {!!doc && (
          <FormWrapper
            key={formKey}
            item={doc}
            onClose={api.close}
            enableFocusTrap={enableFocusTrap}
            showBackdrop={showBackdrop}
            isFormDirtyRef={isFormDirtyRef}
            afterSubmit={afterSubmit}
            afterDelete={afterDelete}
            {...options}
          />
        )}
      </AnimatePresence>
    </worldThemeContext.Provider>
  );
};

export default PriceListFormSlideOver;
